import { defineStore } from 'pinia';
import { StatsService } from '@/services/stats';
import { ProductService } from '@/services/product';
import moment from 'moment';
import _ from 'lodash';

export const stageTypes = {
  /** Created by the demo creation funnel for a lead, until they become a customer */
  LEAD: 'lead',
  DEMO: 'demo',
  ONBOARDING: 'onboarding',
  LIVE: 'live',
  UNDEFINED: 'undefined',
} as const;

/** Comparison chart values for one KPI on the welcome page */
interface StatsChart {
  /** Value of the previous period (e.g. days -180..-90) */
  old: number;
  /** Value of the current period (e.g. last 90 days) */
  new: number;
  /** True while the KPI is being fetched */
  isLoading: boolean;
  /** True when fetching failed or the API returned no value */
  isFailed?: boolean;
}

/**
 * Every write to a `StatsChart` state prop must be a *fresh* object.
 *
 * These actions used to build one object, put it in the state, then mutate that
 * same object and assign it again. The second assignment is a no-op — Vue sees
 * the identical reference and skips the update — and mutating the raw object
 * behind the reactive proxy triggers nothing either, so the KPI kept rendering
 * its skeleton until some unrelated state change happened to re-render the page.
 */
const loadingChart = (): StatsChart => ({
  old: 0,
  new: 0,
  isLoading: true,
  isFailed: false,
});

/** A KPI whose request failed or came back without a value. */
const failedChart = (): StatsChart => ({
  old: 0,
  new: 0,
  isLoading: false,
  isFailed: true,
});

interface WelcomeState {
  /** Onboarding/welcome tasks shown to the user */
  tasks: any[];
  /** Knowledge base teaser items for the current locale/stage */
  knowledge: any[];
  /** Best-practice tips (only items with a non-empty tipBody) */
  bestPractices: any[];
  /** Length of the comparison window in days */
  statsDatePeriod: number;
  /** Positive feedback KPI comparison */
  StatsChartFeedbackPos: StatsChart;
  /** Negative feedback KPI comparison */
  StatsChartFeedbackNeg: StatsChart;
  /** Conversation count KPI comparison */
  StatsChartConversations: StatsChart;
  /** Automation rate KPI comparison (percent) */
  StatsChartAutomation: StatsChart;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/welcome.ts` (namespace `welcome`).
 * IN-MEMORY ONLY — no `vuexPersist` option (the legacy module had no `vuexLocal`/`vuexSession`
 * reducer entry in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - All 8 legacy getters (`tasks`, `knowledge`, `bestPractices`, `statsDatePeriod`,
 *   `StatsChartFeedbackPos`, `StatsChartFeedbackNeg`, `StatsChartConversations`,
 *   `StatsChartAutomation`) were pure identity passthroughs of the state prop of the same name.
 *   Pinia exposes state props directly on the store instance (e.g. `useWelcomeStore().tasks`), so
 *   these getters are DROPPED entirely rather than renamed — consumers read the state prop
 *   directly, which is exactly what the old getter returned. Matches the precedent set in
 *   `src/stores/app.ts` for the same identity-getter situation.
 * - No action/mutation name collisions: the 6 legacy actions (`fetchStatsLive`, `fetchAutomation`,
 *   `fetchConversations`, `fetchFeedbacks`, `getKnowledge`, `getBestPractices`) and the 9 legacy
 *   mutations (`setTasks`, `setKnowledge`, `setBestPractices`, `setStatsLoading`,
 *   `setStatsChartFeedbacksPos`, `setStatsChartFeedbacksNeg`, `setStatsChartConversations`,
 *   `setStatsChartAutomation`, `setStatsDatePeriod`) all have distinct names, so each mutation
 *   simply becomes a same-named action mutating `this` directly — no merging needed.
 * - No cross-namespace (`rootGetters`/`dispatch`/`commit` with `{root:true}`) references exist
 *   anywhere in this module; every `commit`/`dispatch` call was local, so they all become
 *   `this.<name>(...)` calls against this same store.
 * - `setStatsLoading` used to assign the SAME `resetObj` reference to all four chart state props
 *   (a legacy quirk kept for mechanical fidelity); it now hands each one its own object, because
 *   sharing it made the four KPIs alias each other. See `loadingChart` above.
 */
export const useWelcomeStore = defineStore('welcome', {
  state: (): WelcomeState => ({
    tasks: [],
    knowledge: [],
    bestPractices: [],
    statsDatePeriod: 90,
    StatsChartFeedbackPos: {
      old: 0,
      new: 0,
      isLoading: true,
      isFailed: false,
    },
    StatsChartFeedbackNeg: {
      old: 0,
      new: 0,
      isLoading: true,
      isFailed: false,
    },
    StatsChartConversations: {
      old: 0,
      new: 0,
      isLoading: true,
      isFailed: false,
    },
    StatsChartAutomation: {
      old: 0,
      new: 0,
      isLoading: true,
      isFailed: false,
    },
  }),
  actions: {
    fetchStatsLive({ botId }: { botId: string }) {
      // Old: (days * 2) + 1 -> days ( + 1 day)
      // New: days -> today
      const dateRanges = {
        old: [
          moment().add(this.statsDatePeriod * 2 * -1, 'days'),
          moment().add(this.statsDatePeriod * -1, 'days'),
        ],
        new: [moment().add(this.statsDatePeriod * -1, 'days'), moment()],
      };

      this.fetchAutomation({ botId, dateRanges });
      this.fetchConversations({ botId, dateRanges });
      this.fetchFeedbacks({ botId, dateRanges });
    },
    async fetchAutomation({ botId, dateRanges }: { botId: string; dateRanges: any }) {
      this.setStatsChartAutomation(loadingChart());

      try {
        // Old range
        const dataOld: any = await StatsService.getKpi(botId, 'automation', dateRanges.old);
        // New range
        const dataNew: any = await StatsService.getKpi(botId, 'automation', dateRanges.new);

        if (_.isUndefined(dataOld?.automation) || _.isUndefined(dataNew?.automation)) {
          this.setStatsChartAutomation(failedChart());
          return {};
        }

        this.setStatsChartAutomation({
          old: dataOld.automation * 100 || 0,
          new: dataNew.automation * 100 || 0,
          isLoading: false,
          isFailed: false,
        });

        return {};
      } catch (error) {
        this.setStatsChartAutomation(failedChart());
        return {};
      }
    },
    async fetchConversations({ botId, dateRanges }: { botId: string; dateRanges: any }) {
      this.setStatsChartConversations(loadingChart());

      try {
        // Old range
        const dataOld: any = await StatsService.getKpi(botId, 'conversations', dateRanges.old);
        // New range
        const dataNew: any = await StatsService.getKpi(botId, 'conversations', dateRanges.new);

        if (_.isUndefined(dataOld?.conversations) || _.isUndefined(dataNew?.conversations)) {
          this.setStatsChartConversations(failedChart());
          return {};
        }

        this.setStatsChartConversations({
          old: dataOld.conversations || 0,
          new: dataNew.conversations || 0,
          isLoading: false,
          isFailed: false,
        });

        return {};
      } catch (error) {
        this.setStatsChartConversations(failedChart());
        return {};
      }
    },
    async fetchFeedbacks({ botId, dateRanges }: { botId: string; dateRanges: any }) {
      const feedbacksNeg: any = { isLoading: true };
      const feedbacksPos: any = { isLoading: true };

      try {
        // Old range
        const resultOld: any = await StatsService.getFeedbacks(botId, dateRanges.old);
        feedbacksPos.old = resultOld[0].count;
        feedbacksNeg.old = resultOld[1].count;

        // New range
        const resultNew: any = await StatsService.getFeedbacks(botId, dateRanges.new);
        feedbacksPos.new = resultNew[0].count;
        feedbacksNeg.new = resultNew[1].count;

        if (resultOld && resultNew) {
          feedbacksPos.isLoading = false;
          feedbacksNeg.isLoading = false;
          this.setStatsChartFeedbacksPos(feedbacksPos);
          this.setStatsChartFeedbacksNeg(feedbacksNeg);
        } else {
          this.setStatsChartFeedbacksPos(failedChart());
          this.setStatsChartFeedbacksNeg(failedChart());
        }

        return {};
      } catch (error) {
        this.setStatsChartFeedbacksPos(failedChart());
        this.setStatsChartFeedbacksNeg(failedChart());
        return {};
      }
    },
    async getKnowledge({ locale, stage }: { locale: string; stage: string }) {
      const knowledgeItems = await ProductService.getKnowledgeBaseItems(locale, stage);
      this.setKnowledge(knowledgeItems);
      return true;
    },
    async getBestPractices({ locale, stage }: { locale: string; stage: string }) {
      const tips: any = await ProductService.getExpertItems(locale, stage);

      this.setBestPractices(
        Array.isArray(tips) ? tips.filter((tip: any) => {
          return tip.tipBody && tip.tipBody.length > 0;
        }) : [],
      );
      return true;
    },
    setTasks(tasks: any[]) {
      this.tasks = tasks;
    },
    setKnowledge(knowledge: any[]) {
      this.knowledge = knowledge;
    },
    setBestPractices(practices: any[]) {
      this.bestPractices = practices;
    },
    setStatsLoading() {
      this.StatsChartFeedbackPos = loadingChart();
      this.StatsChartFeedbackNeg = loadingChart();
      this.StatsChartConversations = loadingChart();
      this.StatsChartAutomation = loadingChart();
    },
    setStatsChartFeedbacksPos(feedbacksPos: StatsChart) {
      this.StatsChartFeedbackPos = feedbacksPos;
    },
    setStatsChartFeedbacksNeg(feedbacksNeg: StatsChart) {
      this.StatsChartFeedbackNeg = feedbacksNeg;
    },
    setStatsChartConversations(conversations: StatsChart) {
      this.StatsChartConversations = conversations;
    },
    setStatsChartAutomation(automation: StatsChart) {
      this.StatsChartAutomation = automation;
    },
    setStatsDatePeriod(newDays: number) {
      this.statsDatePeriod = newDays;
    },
  },
});
